import React, { useState, useRef, useEffect } from 'react';

const MultiSelectBox = ({
  options = [],
  value = [],
  onChange,
  placeholder = "Select options...",
  label = "",
  name = "",
  useObjects = false,
  singleSelect = false,
  getOptionColor = null
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const selectRef = useRef(null);

  // Close dropdown when clicking outside the component
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = (optionValue) => {
    if (singleSelect) {
      // For single select, just set the selected value or empty if clicking same item
      const newSelected = value.includes(optionValue) ? [] : [optionValue];
      if (onChange) onChange(newSelected);
      setIsOpen(false); // Close dropdown after selection for single select
    } else {
      // Multi-select behavior
      const newSelected = value.includes(optionValue)
        ? value.filter(item => item !== optionValue)
        : [...value, optionValue];

      if (onChange) onChange(newSelected);
    }
  };

  const handleSelectAll = () => {
    if (singleSelect) return; // Don't allow select all for single select

    const allValues = useObjects
      ? options.map(opt => opt.value)
      : options;
    if (onChange) onChange(allValues);
  };

  const handleDeselectAll = () => {
    if (onChange) onChange([]);
  };

  const getDisplayText = () => {
    if (value.length === 0) return placeholder;

    if (singleSelect && value.length > 0) {
      // For single select, show the label of selected item
      const selectedValue = value[0];
      if (useObjects) {
        const selectedOption = options.find(opt => opt.value === selectedValue);
        return selectedOption ? selectedOption.label : selectedValue;
      }
      return selectedValue;
    }

    const totalOptions = options.length;
    if (value.length === totalOptions) return `All (${value.length})`;
    return `${value.length} selected`;
  };

  const displayText = getDisplayText();

  // Calculate dropdown position
  const getDropdownStyle = () => {
    if (!selectRef.current) return {};
    
    const rect = selectRef.current.getBoundingClientRect();
    const isInDialog = document.querySelector('[role="dialog"]')?.contains(selectRef.current);
    
    if (isInDialog) {
      return {
        position: 'fixed',
        top: `${rect.bottom + 4}px`,
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        zIndex: 999999
      };
    }
    
    return {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      zIndex: 100000
    };
  };

  return (
    <div style={{ display: 'inline-block', marginRight: '10px', marginBottom: '10px', verticalAlign: 'top' }} ref={dropdownRef}>
      {label && (
        <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px', fontWeight: 'bold', color: '#fff' }}>
          {label}
        </label>
      )}

      <div
        ref={selectRef}
        className="select-cp"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: '200px',
          cursor: 'pointer',
          position: 'relative',
          padding: '6px 30px 6px 8px',
          border: '1px solid #555',
          borderRadius: '4px',
          backgroundColor: '#2a2a2a',
          fontSize: '14px',
          minHeight: '32px',
          display: 'flex',
          alignItems: 'center',
          color: '#fff'
        }}
      >
        <span style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          color: value.length === 0 ? '#999' : '#fff'
        }}>
          {displayText}
        </span>

        <span style={{
          position: 'absolute',
          right: '8px',
          top: '50%',
          transform: `translateY(-50%) ${isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}`,
          transition: 'transform 0.2s',
          fontSize: '12px',
          pointerEvents: 'none',
          color: '#999'
        }}>
          ▼
        </span>

        {isOpen && (
          <div
            onClick={(e) => e.stopPropagation()}
            className="custom-scrollbar"
            style={{
              ...getDropdownStyle(),
              marginTop: '4px',
              backgroundColor: '#2a2a2a',
              border: '1px solid #555',
              borderRadius: '4px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
              maxHeight: '300px',
              overflowY: 'auto',
              scrollbarWidth: 'thin',
              scrollbarColor: '#555 #2a2a2a'
            }}
          >
            {/* Select/Deselect All buttons */}
            <div style={{
              padding: '8px',
              borderBottom: '1px solid #444',
              display: 'flex',
              gap: '8px'
            }}>
              {!singleSelect && (
                <button
                  onClick={handleSelectAll}
                  style={{
                    flex: 1,
                    padding: '4px 8px',
                    fontSize: '11px',
                    border: '1px solid #555',
                    borderRadius: '3px',
                    backgroundColor: '#3a3a3a',
                    color: '#fff',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#4a4a4a'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#3a3a3a'}
                >
                  Select All
                </button>
              )}
              <button
                onClick={handleDeselectAll}
                style={{
                  flex: 1,
                  padding: '4px 8px',
                  fontSize: '11px',
                  border: '1px solid #555',
                  borderRadius: '3px',
                  backgroundColor: '#3a3a3a',
                  color: '#d32f2f',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#4a4a4a'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#3a3a3a'}
              >
                {singleSelect ? 'Clear' : 'Deselect All'}
              </button>
            </div>

            {/* Checkbox list */}
            {options.length === 0 ? (
              <div style={{ padding: '12px', textAlign: 'center', color: '#999' }}>
                No options
              </div>
            ) : (
              options.map((option) => {
                const optionValue = useObjects ? option.value : option;
                const optionLabel = useObjects ? option.label : option;
                const optionColor = getOptionColor ? getOptionColor(optionValue) : null;

                return (
                  <label
                    key={optionValue}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '8px 12px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      color: '#fff',
                      transition: 'background-color 0.15s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a3a3a'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <input
                      type={singleSelect ? 'radio' : 'checkbox'}
                      checked={value.includes(optionValue)}
                      onChange={() => handleToggle(optionValue)}
                      style={{
                        marginRight: '8px',
                        cursor: 'pointer',
                        width: '16px',
                        height: '16px',
                        flexShrink: '0'
                      }}
                    />
                    {optionColor && (
                      <div
                        style={{
                          width: '12px',
                          height: '12px',
                          borderRadius: '2px',
                          backgroundColor: optionColor,
                          marginRight: '8px',
                          flexShrink: '0'
                        }}
                      />
                    )}
                    <span>{optionLabel}</span>
                  </label>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiSelectBox;